import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Gift } from 'lucide-react';
import { describeVoucherValue } from '@mlm/shared';
import { Button, Card, CardContent, cn } from '@mlm/ui';
import { BirthdayGiftCard } from '../components/birthday-gift-card';
import { CopyButton } from '../components/copy-button';
import { DownloadableQr } from '../components/downloadable-qr';
import { getMyVouchers, type MyVoucher, type MyVoucherProgress } from '../lib/api';
import { fmtDate } from '../lib/format';
import { useTenantConfig } from '../storefront/lib/hooks';

/**
 * The member's gift checks (cashier phase 4; `vouchers` in the API): the ones
 * ready to use, with a code and a QR to show at the counter; how far along
 * they are toward the next one; and the ones already used or lapsed. Named by
 * the tenant's own word (`voucherLabel`). The route is only mounted while the
 * tenant has gift checks on.
 */
export function GiftChecksPage() {
  const label = useTenantConfig().data?.voucherLabel?.trim() || 'Gift Check';
  const plural = label.endsWith('s') ? label : `${label}s`;
  // Always refetched on a visit or on focus: a member shows a code at the
  // counter, and once the cashier has used it the page must not still offer it.
  const q = useQuery({ queryKey: ['my-vouchers'], queryFn: getMyVouchers, staleTime: 0 });
  const data = q.data;
  const nothing =
    !!data && data.usable.length === 0 && data.progress.length === 0 && data.history.length === 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{plural}</h1>
        <p className="text-sm text-muted-foreground">
          Show the code or the QR at the counter and the cashier takes it off your order. Each one
          works once.
        </p>
      </div>

      <BirthdayGiftCard label={label} />

      {q.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center text-sm text-muted-foreground">
            <p>We couldn&apos;t load your {plural.toLowerCase()}.</p>
            <Button size="sm" variant="outline" onClick={() => void q.refetch()}>
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : nothing ? (
        <Card>
          <CardContent className="space-y-3 py-12 text-center">
            <Gift className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No {plural.toLowerCase()} yet. When you earn one it shows up here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Ready to use</h2>
            {data!.usable.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                None right now.
                {data!.progress.length > 0
                  ? ' See below for how close you are to the next one.'
                  : ''}
              </p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {data!.usable.map((v) => (
                  <UsableCard key={v.id} v={v} />
                ))}
              </div>
            )}
          </section>

          {data!.progress.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold">Earning</h2>
              <div className="grid gap-3 md:grid-cols-2">
                {data!.progress.map((p) => (
                  <ProgressCard key={p.programId} p={p} label={label} />
                ))}
              </div>
            </section>
          )}

          {data!.history.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold">Used and expired</h2>
              <Card>
                <CardContent className="divide-y p-0">
                  {data!.history.map((v) => (
                    <div
                      key={v.id}
                      className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium">{v.programName}</p>
                        <p className="text-xs text-muted-foreground">
                          <span className="font-mono">{v.code}</span> · {describeVoucherValue(v)}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">{historyLine(v)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function historyLine(v: MyVoucher): string {
  if (v.status === 'redeemed') return v.redeemedAt ? `Used ${fmtDate(v.redeemedAt)}` : 'Used';
  if (v.status === 'revoked') return 'Cancelled';
  return v.expiresAt ? `Expired ${fmtDate(v.expiresAt)}` : 'Expired';
}

function appliesTo(v: { appliesToAll: boolean; productNames: string[] }): string {
  if (v.appliesToAll) return 'Your whole order';
  if (v.productNames.length === 0) return 'Selected products';
  return v.productNames.join(', ');
}

function UsableCard({ v }: { v: MyVoucher }) {
  const [showQr, setShowQr] = useState(false);
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold">{v.programName}</p>
            {v.description && <p className="text-sm text-muted-foreground">{v.description}</p>}
          </div>
          <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
            {describeVoucherValue(v)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">For:</span> {appliesTo(v)}
        </p>
        <div className="flex items-center gap-1 rounded-md border bg-muted/40 px-3 py-2">
          <span className="flex-1 font-mono text-lg tracking-wider">{v.code}</span>
          <CopyButton value={v.code} title="Copy code" label="Code copied" className="h-8 w-8" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{v.expiresAt ? `Valid until ${fmtDate(v.expiresAt)}` : 'Does not expire'}</span>
          <Button size="sm" variant="outline" onClick={() => setShowQr((s) => !s)}>
            {showQr ? 'Hide QR' : 'Show QR'}
          </Button>
        </div>
        {showQr && (
          <DownloadableQr
            value={v.code}
            size={176}
            fileName={`gift-check-${v.code}`}
            caption="The cashier can scan this instead of typing the code."
          />
        )}
      </CardContent>
    </Card>
  );
}

function ProgressCard({ p, label }: { p: MyVoucherProgress; label: string }) {
  const done = Math.min(p.count, p.earnCount);
  const left = Math.max(p.earnCount - p.count, 0);
  const counts = p.allProducts
    ? 'Every item you buy counts.'
    : p.earnProductNames.length
      ? `Counts: ${p.earnProductNames.join(', ')}`
      : '';
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold">{p.programName}</p>
            {p.description && <p className="text-sm text-muted-foreground">{p.description}</p>}
          </div>
          <span className="shrink-0 rounded-full bg-muted px-2.5 py-1 text-xs font-semibold">
            {describeVoucherValue(p)}
          </span>
        </div>
        {p.earnCount <= 12 ? (
          <div className="flex flex-wrap gap-1.5" aria-label={`${done} of ${p.earnCount}`}>
            {Array.from({ length: p.earnCount }, (_, i) => (
              <span
                key={i}
                className={cn(
                  'h-6 w-6 rounded-full border',
                  i < done ? 'border-primary bg-primary' : 'border-dashed bg-background',
                )}
              />
            ))}
          </div>
        ) : (
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary"
              style={{ width: `${Math.round((done / p.earnCount) * 100)}%` }}
            />
          </div>
        )}
        <p className="text-sm">
          <span className="font-semibold">
            {done} of {p.earnCount}
          </span>{' '}
          <span className="text-muted-foreground">
            {left === 1
              ? `· 1 more for your next ${label}`
              : `· ${left} more for your next ${label}`}
          </span>
        </p>
        {counts && <p className="text-xs text-muted-foreground">{counts}</p>}
      </CardContent>
    </Card>
  );
}
